import { useState } from 'react';
import ListView from '../components/ListView';
import SearchBar from '../components/SearchBar';
import PaperDetailsModal from '../components/modals/PaperDetailsModal';
import { Study } from '../types/Study';
import { useAuth } from '../context/AuthContext';
import SideMenu from '../components/Menu';
import { useNavigate } from 'react-router-dom';
import LoadingModal from '../components/modals/LoadingModal';

const Dashboard = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingPaperAnalytics, setLoadingPaperAnalytics] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageRange, setPageRange] = useState<Array<number>>([1, 9]);
  const [numResults, setNumResults] = useState<number>(0);
  const [selectedStudy, setSelectedStudy] = useState<Study | null>(null);
  const [bookmarkSuccess, setBookmarkSuccess] = useState<string | null>(null);
  const [bookmarkError, setBookmarkError] = useState<string | null>(null);

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  interface FilterConfig {
    name: keyof FilterValues;
    label: string;
    type: 'text' | 'select';
    options?: string[];
  }

  interface FilterValues {
    title: string;
    institution: string;
    author: string;
    year: string;
    sort: string;
  }

  interface Paper {
    id?: string;
    title: string;
    authors?: string[];
    concepts?: string[];
    publication_date?: string;
    open_access?: boolean;
    doi?: string;
    url?: string;
  }

  const [filterValues, setFilterValues] = useState<FilterValues>({
    title: '',
    institution: '',
    author: '',
    year: '',
    sort: '',
  });

  const filterConfig: FilterConfig[] = [
    { name: 'title', label: 'Title', type: 'text' },
    { name: 'institution', label: 'Institution', type: 'text' },
    { name: 'author', label: 'Author', type: 'text' },
    { name: 'year', label: 'Publication Year', type: 'text' },
    {
      name: 'sort',
      label: 'Sort Order',
      type: 'select',
      options: ['ascending', 'descending'],
    },
  ];

  const handleSearch = async (pageNumber: string) => {
    if (pageNumber === '1') setPageRange([1, 9]);

    if (pageNumber !== currentPage.toString()) setCurrentPage(parseInt(pageNumber));

    if ([searchQuery, ...Object.values(filterValues)].every(value => value === '')) {
      console.log('No search query or filters provided');
      return;
    }

    setLoading(true);

    try {
      const queryParams = new URLSearchParams();

      let hasFilter = false;
      for (const [key, value] of Object.entries(filterValues)) {
        if (value) {
          queryParams.set(key, value);
          hasFilter = true;
        }
      }

      let url = `https://academichub-backend.onrender.com/search?page=${pageNumber}&`;
      if (searchQuery) {
        url += `query=${searchQuery}`;
      }
      url += hasFilter ? `&${queryParams.toString()}` : queryParams.toString();

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Search request failed');
      }

      const data = await response.json();
      setSearchResults(data.results || []);
      setNumResults(data.count);

      const numPages = Math.ceil(data.count / 25);

      if (numPages < 10) {
        setPageRange([1, numPages]);
      }
    } catch (error) {
      console.error('Error during search:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageRange = (next: boolean) => () => {
    if (next) {
      if (pageRange[1] < Math.ceil(numResults / 10)) {
        const newRange = [pageRange[0] + 1, pageRange[1] + 1];
        setPageRange(newRange);

        if (currentPage <= pageRange[0]) {
          handleSearch(newRange[0].toString());
        }
      }
    } else {
      if (pageRange[0] > 1) {
        const newRange = [pageRange[0] - 1, pageRange[1] - 1];
        setPageRange(newRange);

        if (currentPage >= pageRange[1]) {
          handleSearch(newRange[1].toString());
        }
      }
    }
  };

  const handlePaperAnalytics = async (paperId: string | undefined) => {
    if (!paperId) return;

    setLoadingPaperAnalytics(true);

    try {
      const url = `https://academichub-backend.onrender.com/paperanalytics?id=${paperId}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch paper analytics');
      }

      const data = await response.json();
      console.log(data); // debugging log
      setSelectedStudy(data);
    } catch (error) {
      console.error('Error fetching paper analytics:', error);
    } finally {
      setLoadingPaperAnalytics(false);
    }
  };

  const handleBookmarkPaper = async (paper: Paper) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Clear any previous messages
    setBookmarkSuccess(null);
    setBookmarkError(null);

    try {
      // Get the OpenAlex ID from the URL if it exists
      const paperId = paper.url ? paper.url.split('/').pop() : paper.id;

      if (!paperId) {
        throw new Error('Paper ID not found');
      }

      // Convert authors array to comma-separated string if needed
      const paperData = {
        id: paperId,
        title: paper.title,
        authors: Array.isArray(paper.authors) ? paper.authors.join(', ') : paper.authors,
        description: paper.concepts ? paper.concepts.join(', ') : '',
        source: paper.doi ? new URL(paper.doi).hostname : 'Unknown source',
        publicationYear: paper.publication_date
          ? new Date(paper.publication_date).getFullYear()
          : undefined,
        doi: paper.doi ? paper.doi.replace('https://doi.org/', '') : undefined,
        openAccess: paper.open_access,
      };

      const response = await fetch('https://academichub-backend.onrender.com/papers/bookmark', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paperId,
          paperData,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to bookmark paper');
      }

      setBookmarkSuccess(`"${paper.title}" bookmarked successfully!`);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setBookmarkSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error bookmarking paper:', err);
      setBookmarkError(err instanceof Error ? err.message : 'Failed to bookmark paper');

      // Clear error message after 3 seconds
      setTimeout(() => {
        setBookmarkError(null);
      }, 3000);
    }
  };

  return (
    <>
      {isAuthenticated && <SideMenu />}
      <div id="dashboard" className="p-3 h-screen bg-accent w-full">
        <div className="bg-white rounded-lg h-full flex flex-col items-center justify-center relative">
          {!isAuthenticated && (
            <div className="absolute top-0 right-0 flex m-4">
              <button
                type="submit"
                className="text-sm me-2 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                onClick={() => navigate('/login')}
              >
                Log in
              </button>
              <button
                type="submit"
                className="text-sm bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                onClick={() => navigate('/signup')}
              >
                Sign up
              </button>
            </div>
          )}
          <div className="pt-6 px-2 sm:px-0 pb-2 flex-shrink-0">
            <h1 className="px-3 text-4xl 2xl:text-6xl font-bold text-center">Begin your search</h1>
            <h2 className="text-center px-3 text-xl 2xl:text-2xl mt-4">
              Search across over{' '}
              <span className="accent-highlight">14.7 million ORCiD records</span> currently active
            </h2>
            <SearchBar
              filters={filterConfig}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filterValues={filterValues}
              setFilterValues={setFilterValues}
              onSearch={() => handleSearch('1')}
            />
          </div>

          {/* Notification Banner */}
          {(bookmarkSuccess || bookmarkError) && (
            <div
              className={`fixed top-4 right-4 z-50 p-3 rounded-md shadow-md transition-opacity duration-300 ${
                bookmarkSuccess ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              {bookmarkSuccess || bookmarkError}
            </div>
          )}

          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="loader animate-spin rounded-full h-16 w-16 border-t-4 border-accent"></div>
            </div>
          ) : searchResults.length > 0 ? (
            <div
              id="search-results"
              className="flex-1 flex flex-col px-8 lg:px-14 py-4 overflow-hidden"
            >
              <h2 className="text-2xl 2xl:text-3xl font-bold mb-2 flex-shrink-0">
                Search results <span className="text-accent">({numResults} results)</span>
              </h2>
              <div className="flex-1 overflow-y-auto">
                <ListView
                  results={searchResults}
                  onSelectStudy={handlePaperAnalytics}
                  onBookmarkPaper={handleBookmarkPaper}
                  isAuthenticated={isAuthenticated}
                />
              </div>
              <div className="flex justify-center mt-4">
                <div className="flex">
                  <button
                    className={`mx-2 ${pageRange[0] === 1 && 'hidden'}`}
                    disabled={pageRange[0] === 1}
                    onClick={handlePageRange(false)}
                  >
                    Previous
                  </button>
                  {Array.from(
                    { length: pageRange[1] - pageRange[0] + 1 },
                    (_, i) => pageRange[0] + i
                  ).map(pageNumber => (
                    <button
                      className={`mx-2 hover:text-blue-600 ${
                        currentPage === pageNumber ? 'text-accent' : ''
                      }`}
                      key={pageNumber}
                      onClick={() => handleSearch(pageNumber.toString())}
                    >
                      {pageNumber}
                    </button>
                  ))}
                  <button
                    className={`mx-2 ${pageRange[1] >= Math.ceil(numResults / 25) && 'hidden'}`}
                    onClick={handlePageRange(true)}
                    disabled={pageRange[1] >= Math.ceil(numResults / 25)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div /> // Empty spacer when no results
          )}
        </div>
        {selectedStudy && (
          <PaperDetailsModal
            isOpen={!!selectedStudy}
            onClose={() => setSelectedStudy(null)}
            study={selectedStudy}
          />
        )}
        {loadingPaperAnalytics && <LoadingModal onClose={() => setSelectedStudy(null)} />}
      </div>
    </>
  );
};
export default Dashboard;
